import { useEffect, useState } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, MoveUp, MoveDown, Loader2 } from "lucide-react";
import { getServiceCategories } from "@/services/supabaseService";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import * as lucideIcons from "lucide-react";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Category {
  id: string;
  name: string;
  description: string;
  icon: string | null;
  image_url: string;
  order_num: number;
}

export default function ServiceCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{id: string, name: string} | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      console.log("Fetching service categories");
      const data = await getServiceCategories();
      console.log("Service categories fetched:", data);
      setCategories(data);
    } catch (error) {
      console.error("Kategorileri getirme hatası:", error);
      toast({
        title: "Hata",
        description: "Kategorileri getirirken bir sorun oluştu.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const moveCategory = async (id: string, direction: "up" | "down") => {
    const currentCategories = [...categories];
    const index = currentCategories.findIndex(category => category.id === id);
    
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === currentCategories.length - 1)
    ) {
      return;
    }
    
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    
    // Geçici olarak sıralamayı güncelle
    [currentCategories[index], currentCategories[swapIndex]] = [
      currentCategories[swapIndex], 
      currentCategories[index]
    ];
    
    // Sıralama numaralarını güncelle
    const updatedCategories = currentCategories.map((category, i) => ({
      ...category,
      order_num: i + 1
    }));
    
    setCategories(updatedCategories);
    
    // Veritabanında güncelle
    try {
      console.log("Updating category order:", {
        index: updatedCategories[index],
        swapIndex: updatedCategories[swapIndex]
      });
      
      // İki kategoriyi de güncelle
      const updates = [
        {
          id: updatedCategories[index].id,
          order_num: updatedCategories[index].order_num
        },
        {
          id: updatedCategories[swapIndex].id,
          order_num: updatedCategories[swapIndex].order_num
        }
      ];
      
      for (const update of updates) {
        const { error } = await supabase
          .from('service_categories')
          .update({ order_num: update.order_num })
          .eq('id', update.id);
          
        if (error) {
          console.error("Update error:", error);
          throw error;
        }
      }
    } catch (error) {
      console.error("Sıralama güncelleme hatası:", error);
      toast({
        title: "Hata",
        description: "Kategori sırası güncellenirken bir sorun oluştu.",
        variant: "destructive",
      });
      // Hatadan sonra verileri yeniden yükle
      fetchCategories();
    }
  };

  const confirmDelete = (category: Category) => {
    const parsedName = JSON.parse(category.name);
    const categoryName = parsedName.tr || "Bu kategori";
    setCategoryToDelete({
      id: category.id,
      name: categoryName
    });
    setDeleteDialogOpen(true);
  };

  const deleteCategory = async (id: string) => {
    try {
      console.log("Deleting category:", id);
      const { error } = await supabase
        .from('service_categories')
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error("Delete error:", error);
        throw error;
      }
      
      toast({
        title: "Başarılı",
        description: "Kategori başarıyla silindi.",
      });
      
      // Listeyi güncelle
      setCategories(categories.filter(category => category.id !== id));
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error("Kategori silme hatası:", error);
      toast({
        title: "Hata",
        description: "Kategori silinirken bir sorun oluştu.",
        variant: "destructive",
      });
    }
  };

  const getLocalizedContent = (content: string | any): string => {
    if (!content) return "";
    
    // Eğer zaten string değilse (yani JSON objesiyse)
    if (typeof content !== 'string') {
      return content.tr || content.en || "";
    }
    
    // String ise JSON olarak parse etmeyi dene
    try {
      const parsed = JSON.parse(content);
      return parsed.tr || parsed.en || "";
    } catch (error) {
      // Parse işlemi başarısız olursa orijinal stringi döndür
      console.log("JSON parse hatası, orijinal içerik döndürülüyor:", error);
      return content;
    }
  };

  return (
    <ContentLayout 
      title="Hizmet Kategorileri" 
      backLink="/dashboard"
      actionButton={{
        label: "Yeni Kategori Ekle",
        href: "/content/services/categories/new"
      }}
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-muted-foreground">Henüz kategori bulunmuyor.</p>
          <Button 
            className="mt-4" 
            variant="outline" 
            asChild
          >
            <Link to="/content/services/categories/new">Yeni Kategori Ekle</Link>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <Card key={category.id}>
              <CardContent className="p-6">
                <div className="aspect-video relative mb-4">
                  <img
                    src={category.image_url}
                    alt={getLocalizedContent(category.name)}
                    className="object-cover w-full h-full rounded-lg"
                  />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  {category.icon && (() => {
                    const IconComponent = lucideIcons[category.icon as keyof typeof lucideIcons];
                    if (!IconComponent) {
                      console.warn(`Icon not found in lucideIcons: ${category.icon}`);
                      return <lucideIcons.Image className="h-5 w-5 text-muted-foreground" />; // Varsayılan bir ikon göster
                    }
                    return <IconComponent className="h-5 w-5" />;
                  })()}
                  <h3 className="font-semibold">{getLocalizedContent(category.name)}</h3>
                </div>
                <div className="text-sm text-muted-foreground mb-4 line-clamp-3" dangerouslySetInnerHTML={{ __html: getLocalizedContent(category.description) }} />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => moveCategory(category.id, "up")}>
                      <MoveUp className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => moveCategory(category.id, "down")}>
                      <MoveDown className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" asChild>
                      <Link to={`/content/services/categories/${category.id}/edit`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => confirmDelete(category)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kategoriyi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              "{categoryToDelete?.name}" kategorisini silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCategoryToDelete(null)}>İptal</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => categoryToDelete && deleteCategory(categoryToDelete.id)} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ContentLayout>
  );
}
